import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import MonomerMappingForm, {MonomerMapping} from 'bulk-import/panes/MonomerMappingForm';
import {RegistryLabelingStrategy} from 'constants/RegistryConstants';
import {RegistrationTableInsertType} from 'notebook/integrations/registry/RegistrationTableConstants';
import {INVALID_MONOMER_MAPPING_CONFIG} from 'notebook/structured-tables/bases-columns/BasesColumnConstants';
import useSelectFolderOptionForTable, {
  StructuredTableFolderConfig,
} from 'notebook/structured-tables/components/useSelectFolderOptionForTable';
import SchemaLabelingStrategyInput from 'registry/labeling-strategies/SchemaLabelingStrategyInput';
import {ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';

interface RegistrationTableSettingsModalProps {
  labelingStrategy: RegistryLabelingStrategy | null;
  monomerMapping?: MonomerMapping | typeof INVALID_MONOMER_MAPPING_CONFIG | null;
  insertType?: RegistrationTableInsertType;
  folderConfig?: StructuredTableFolderConfig;
  showMonomerMappingForm?: boolean;
  locked: boolean;
  tagSchemaId: string;
  onRequestClose: () => void;
  onRequestSubmit: (registrationTableSettingsValues: RegistrationTableSettingsModalValues) => void;
}

export interface RegistrationTableSettingsModalValues {
  labelingStrategy: RegistryLabelingStrategy | null;
  monomerMapping?: MonomerMapping | null;
  folderConfig?: StructuredTableFolderConfig;
}

export default function RegistrationTableSettingsModal(
  props: RegistrationTableSettingsModalProps
): JSX.Element {
  const [labelingStrategy, setLabelingStrategy] = useState<RegistryLabelingStrategy | null>(
    props.labelingStrategy
  );
  const [monomerMapping, setMonomerMapping] = useState(
    props.monomerMapping !== INVALID_MONOMER_MAPPING_CONFIG ? props.monomerMapping : null
  );

  const {selectedFolderConfig, folderOptionSelector, isInvalidFolderConfig} = useSelectFolderOptionForTable({
    initialFolderConfig: props.folderConfig,
    itemTypeSupportsFolders: true,
    isReadOnly: props.locked,
    isForCreate: props.insertType === RegistrationTableInsertType.CREATE_NEW_ENTITIES,
    projectIsRequired: BenchlingConfig.ENABLE_PROJECTS_REQUIRED_FOR_REGISTRY_FILES,
  });

  const _handleRequestSubmit = (): void => {
    if (labelingStrategy != null || monomerMapping != null || props.folderConfig != null) {
      props.onRequestSubmit({
        labelingStrategy,
        monomerMapping,
        folderConfig: props.folderConfig != null ? selectedFolderConfig : undefined,
      });
    }
  };

  return (
    <Modal
      title="Registration table settings"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={_handleRequestSubmit}
      buttons={[
        {
          text: 'Save',
          disabled: props.locked || isInvalidFolderConfig,
          style: ButtonStyle.SECONDARY,
          submit: true,
        },
        {text: 'Cancel', cancel: true},
      ]}
    >
      <SchemaLabelingStrategyInput
        disabled={props.locked}
        labelingStrategy={labelingStrategy}
        tagSchemaId={props.tagSchemaId}
        onChange={(labelingStrategy: RegistryLabelingStrategy) => {
          setLabelingStrategy(labelingStrategy);
        }}
      />
      {props.showMonomerMappingForm && (
        <>
          <MonomerMappingForm
            onSetMonomerMapping={setMonomerMapping}
            monomerMapping={monomerMapping}
            disabled={props.locked}
            sectionHeader={<FieldLabel label="Nucleotide interpretation settings" />}
          />
        </>
      )}
      {props.folderConfig != null && folderOptionSelector}
    </Modal>
  );
}
